import { useCallback, useEffect, useRef, useState } from 'react';

import { CREATE_PARAM } from 'Routes';
import { ChartType, PlayerStat } from 'types/CodegenReportTypes';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { UnitSystem } from 'types/StartupTypes';
import { useGetProfileCache } from './startupHooks';
import usePitcherReportStore from './PlayerReport/Pitcher/reportStore';
import useBatterReportStore from './PlayerReport/Batter/reportStore';
import usePitcherSingleSessionReportStore from './SessionReport/PitcherSingle/reportStore';
import usePitcherSessionReportStore from './SessionReport/Pitcher/reportStore';
import useBatterSessionReportStore from './SessionReport/Batter/reportStore';
import usePracticePitcherSessionReportStore from './PracticeReport/Pitcher/reportStore';
import usePracticePitcherSingleSessionReportStore from './PracticeReport/PitcherSingle/reportStore';

export const useReportId = () => {
  const { reportId } = useParams();

  return { reportId, isCreate: reportId === CREATE_PARAM } as
    | { reportId: typeof CREATE_PARAM; isCreate: true }
    | { reportId: string | undefined; isCreate: false };
};

export const useChartDataDisplay = <ChartData>(
  chartData: ChartData | undefined,
  simulateChartData: (isLoading: boolean) => ChartData,
  isLoading: boolean
) => {
  const [chartDataDisplay, setChartDataDisplay] = useState<ChartData>(chartData ?? simulateChartData(false));
  const intervalIdRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = undefined;
      }
    };
  }, []);

  // This useEffect is a bit tricky to get right – changing it can easily cause an infinite loop, proceed with caution.
  useEffect(() => {
    if (isLoading) {
      // If an interval is already running, clear it before setting a new one
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      setChartDataDisplay(simulateChartData(true));
      const interval = setInterval(() => {
        setChartDataDisplay(simulateChartData(true));
      }, 1500);
      intervalIdRef.current = interval; // Store the new interval ID in the ref
    } else {
      // Clear the interval if it's running
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = undefined; // Clear the ref
      }
      setChartDataDisplay(chartData ?? simulateChartData(false));
    }
  }, [isLoading, chartData, simulateChartData]);

  return chartDataDisplay;
};

export const useChartTitle = () => {
  const { t } = useTranslation();

  const { selectedConfgValues: pitcherReportSelectedConfigValues } = usePitcherReportStore();
  const { selectedConfgValues: batterReportSelectedConfigValues } = useBatterReportStore();
  const { selectedConfgValues: pitcherSingleSessionReportSelectedConfigValues } = usePitcherSingleSessionReportStore();
  const { selectedConfgValues: pitcherSessionReportSelectedConfigValues } = usePitcherSessionReportStore();
  const { selectedConfgValues: batterSessionReportSelectedConfigValues } = useBatterSessionReportStore();
  const { selectedConfgValues: practicePitcherSessionReportSelectedConfigValues } = usePracticePitcherSessionReportStore();
  const { selectedConfgValues: practicePitcherSingleSessionReportSelectedConfigValues } = usePracticePitcherSingleSessionReportStore();

  const unitSystem = useGetUnitSystem();

  const getChartTitle = useCallback(
    (chartId: ChartType, chartConfig: any) => {
      switch (chartId) {
        case 'PitcherHeatmapChart':
        case 'BatterHeatmapChart':
        case 'PitcherSingleSessionHeatmapChart':
        case 'PitcherSessionHeatmapChart':
        case 'BatterSessionHeatmapChart':
        case 'PracticeBatterSessionHeatmapChart':
        case 'PracticePitcherSessionHeatmapChart':
          return (chartConfig.viewDataAs === 'PitchLocation' ? t('PitchLocation') : t(chartConfig.playerStat)) + ' ' + t('heatmap');
        case 'PitcherScatterChart_1':
        case 'PitcherScatterChart_2':
        case 'PitcherScatterChart_3':
        case 'PitcherSessionScatterChart_1':
        case 'PitcherSessionScatterChart_2':
        case 'PitcherSessionScatterChart_3':
        case 'PitcherSingleSessionScatterChart_1':
        case 'PitcherSingleSessionScatterChart_2':
        case 'PitcherSingleSessionScatterChart_3':
        case 'PracticeBatterSessionScatterChart_1':
        case 'PracticeBatterSessionScatterChart_2':
        case 'PracticePitcherSessionScatterChart':
        case 'PracticePitcherSingleSessionScatterChart':
          return t(chartConfig.xAxis) + ' vs ' + t(chartConfig.yAxis);
        case 'PitchTypeStatsChart':
          return t('ChartTitle.' + chartId) + ' ' + t(pitcherReportSelectedConfigValues.pitchGrouping);
        case 'BatterUsedPitchTypesChart':
          return t('ChartTitle.' + chartId) + ' ' + t(batterReportSelectedConfigValues.pitchGrouping);
        case 'PitcherSingleSessionStatsByPitchTypeChart':
        case 'PitcherSingleSessionPitchTypeRangesChart':
          return t('ChartTitle.' + chartId) + ' ' + t(pitcherSingleSessionReportSelectedConfigValues.pitchGrouping);
        case 'PitcherSessionStatsByPitchTypeChart':
        case 'PitcherSessionPitchTypeRangesChart':
          return t('ChartTitle.' + chartId) + ' ' + t(pitcherSessionReportSelectedConfigValues.pitchGrouping);
        case 'PracticePitcherSessionStatsByPitchTypeChart':
        case 'PracticePitcherSessionPitchTypeRangesChart':
          return t('ChartTitle.' + chartId) + ' ' + t(practicePitcherSessionReportSelectedConfigValues.pitchGrouping);
        case 'PracticePitcherSingleSessionStatsByPitchTypeChart':
        case 'PracticePitcherSingleSessionPitchTypeRangesChart':
          return t('ChartTitle.' + chartId) + ' ' + t(practicePitcherSingleSessionReportSelectedConfigValues.pitchGrouping);
        case 'PitcherSingleSessionPitchByPitchLineChart_1':
        case 'PitcherSingleSessionPitchByPitchLineChart_2':
        case 'PitcherSingleSessionPitchByPitchLineChart_3':
        case 'PitcherSingleSessionPitchByPitchLineChart_4':
        case 'PracticeBatterSingleSessionPitchByPitchLineChart_1':
        case 'PracticeBatterSingleSessionPitchByPitchLineChart_2':
        case 'PracticePitcherSessionPitchByPitchLineChart_1':
        case 'PracticePitcherSessionPitchByPitchLineChart_2':
        case 'PracticePitcherSessionPitchByPitchLineChart_3':
        case 'PracticePitcherSessionPitchByPitchLineChart_4':
        case 'PracticePitcherSingleSessionPitchByPitchLineChart_1':
        case 'PracticePitcherSingleSessionPitchByPitchLineChart_2':
        case 'PracticePitcherSingleSessionPitchByPitchLineChart_3':
        case 'PracticePitcherSingleSessionPitchByPitchLineChart_4':
          return t(chartConfig.yAxis) + ' ' + t('byPitch');
        case 'PitcherSessionTimelineChart_1':
        case 'PitcherSessionTimelineChart_2':
        case 'PitcherSessionTimelineChart_3':
        case 'PitcherSessionTimelineChart_4':
          return t(chartConfig.yAxis) + ' ' + t('byGame');
        case 'BatterSprayChart':
        case 'BatterSessionSprayChart':
        case 'PracticeBatterSessionSprayChart':
          return chartConfig.viewChartAs === 'SprayChart' ? t('ChartTitle.BatterSprayChart') : t('ChartTitle.BatterSprayZoneChart');
        case 'BatterSessionStatsByPitchTypeChart':
          return t('ChartTitle.' + chartId) + ' ' + t(batterSessionReportSelectedConfigValues.pitchGrouping);
        case 'BatterSingleSessionBattingStatChart_1':
        case 'BatterSingleSessionBattingStatChart_2':
        case 'BatterSingleSessionBattingStatChart_3':
        case 'BatterSingleSessionBattingStatChart_4':
        case 'BatterSingleSessionBattingStatChart_5':
        case 'BatterSingleSessionBattingStatChart_6':
        case 'PracticeBatterSessionBattingStatChart_1':
        case 'PracticeBatterSessionBattingStatChart_2':
        case 'PracticeBatterSessionBattingStatChart_3':
        case 'PracticeBatterSessionBattingStatChart_4':
        case 'PracticeBatterSessionBattingStatChart_5':
        case 'PracticeBatterSessionBattingStatChart_6':
        case 'PracticeBatterSingleSessionBattingStatChart_1':
        case 'PracticeBatterSingleSessionBattingStatChart_2':
        case 'PracticeBatterSingleSessionBattingStatChart_3':
        case 'PracticeBatterSingleSessionBattingStatChart_4':
        case 'PracticeBatterSingleSessionBattingStatChart_5':
        case 'PracticeBatterSingleSessionBattingStatChart_6':
          return (chartConfig.playerStat as PlayerStat) === 'BattingAverage' ? t('HitsPerAtBats') : t(chartConfig.playerStat);
        case 'BatterSessionTimelineChart_1':
        case 'BatterSessionTimelineChart_2':
        case 'BatterSessionTimelineChart_3':
        case 'BatterSessionTimelineChart_4':
        case 'PracticeBatterSessionTimelineChart_1':
        case 'PracticeBatterSessionTimelineChart_2':
          return t(chartConfig.playerStat) + ' ' + t('OverTime');
        default:
          return t('ChartTitle.' + chartId);
      }
    },
    [
      pitcherReportSelectedConfigValues,
      batterReportSelectedConfigValues,
      pitcherSingleSessionReportSelectedConfigValues,
      pitcherSessionReportSelectedConfigValues,
      batterSessionReportSelectedConfigValues,
      practicePitcherSessionReportSelectedConfigValues,
      practicePitcherSingleSessionReportSelectedConfigValues,
    ]
  );

  const getChartSubTitle = useCallback((chartId: ChartType, chartConfig: any) => {
    switch (chartId) {
      case 'PitcherHeatmapChart':
      case 'BatterHeatmapChart':
      case 'PitcherSingleSessionHeatmapChart':
      case 'PitcherSessionHeatmapChart':
      case 'BatterSessionHeatmapChart':
      case 'PracticeBatterSessionHeatmapChart':
      case 'PracticePitcherSessionHeatmapChart':
        if (chartConfig.viewDataAs === 'PlayerStat' && chartConfig.playerStat === 'ExitSpeed') {
          return unitSystem === 'Metric' ? 'kph' : 'mph';
        }
      case 'PitcherSingleSessionPitchTypeRangesChart':
      case 'PitcherSessionPitchTypeRangesChart':
      case 'PracticePitcherSessionPitchTypeRangesChart':
      case 'PracticePitcherSingleSessionPitchTypeRangesChart':
        return t(chartConfig.selectedMeasurementOption);
      case 'BatterSingleSessionPitchLocationChart':
      case 'BatterSingleSessionContactPointChart':
        return t('pitchOfGame');
      case 'UmpireStrikeZoneChart':
        return t('BatterView');
      case 'PracticePitcherSessionTiltChart_1':
      case 'PracticePitcherSessionTiltChart_2':
      case 'PracticePitcherSingleSessionTiltChart_1':
      case 'PracticePitcherSingleSessionTiltChart_2':
        return t(chartConfig.axis);
      default:
        return undefined;
    }
  }, []);

  const getChartSidebarSubTitle = useCallback((chartId: ChartType, chartConfig: any) => {
    switch (chartId) {
      case 'PitchingStatsChart':
      case 'PitchTypeStatsChart':
      case 'PitcherBallTrackingStatsChart':
      case 'BatterUsedPitchTypesChart':
      case 'BatterBallStrikeSummaryChart':
      case 'BattingStatsChart':
      case 'BatterSingleSessionGameStatsChart':
      case 'BatterSingleSessionPitchByPitchStatsChart':
      case 'BatterSessionStatsByPitchTypeChart':
      case 'PitcherSingleSessionStatsByPitchTypeChart':
      case 'PitcherSingleSessionPitchByPitchStatsChart':
      case 'PitcherSingleSessionGameStatsChart':
      case 'PitcherSessionStatsByPitchTypeChart':
      case 'PracticeBatterSessionSummaryChart':
      case 'PracticeBatterSingleSessionSummaryChart':
      case 'PracticeBatterSingleSessionSummaryAggregatedChart':
      case 'PracticePitcherSessionStatsByPitchTypeChart':
      case 'PracticePitcherSingleSessionStatsByPitchTypeChart':
      case 'PracticePitcherSingleSessionPitchByPitchStatsChart':
        return chartConfig.visibleStats?.length ? `${chartConfig.visibleStats.length} ${t('stats')}` : '';

      case 'PitcherScatterChart_1':
      case 'PitcherScatterChart_2':
      case 'PitcherScatterChart_3':
      case 'PitcherSessionScatterChart_1':
      case 'PitcherSessionScatterChart_2':
      case 'PitcherSessionScatterChart_3':
      case 'PitcherSingleSessionScatterChart_1':
      case 'PitcherSingleSessionScatterChart_2':
      case 'PitcherSingleSessionScatterChart_3':
      case 'PracticeBatterSessionScatterChart_1':
      case 'PracticeBatterSessionScatterChart_2':
      case 'PracticePitcherSingleSessionScatterChart':
        return chartConfig.xAxis && chartConfig.yAxis ? `${t(chartConfig.xAxis)}, ${t(chartConfig.yAxis)}` : '';

      case 'PitcherPercentileRankingsChart':
      case 'BatterPercentileRankingsChart':
        return chartConfig.viewDataAs && chartConfig.visibleStats?.length
          ? `${t(chartConfig.viewDataAs)}, ${chartConfig.visibleStats.length} ${t('stats')}`
          : '';

      case 'BatterSingleSessionPitchLocationChart':
        return chartConfig.pitchLocationBy ? t(chartConfig.pitchLocationBy) : '';

      case 'BatterSingleSessionContactPointChart':
        return chartConfig.contactPointBy ? t(chartConfig.contactPointBy) : '';

      case 'PitcherSingleSessionPitchTypeRangesChart':
      case 'PitcherSessionPitchTypeRangesChart':
      case 'PracticePitcherSessionPitchTypeRangesChart':
      case 'PracticePitcherSingleSessionPitchTypeRangesChart':
        return chartConfig.selectedMeasurementOption ? t(chartConfig.selectedMeasurementOption) : '';
      case 'PracticePitcherSessionTiltChart_1':
      case 'PracticePitcherSessionTiltChart_2':
      case 'PracticePitcherSingleSessionTiltChart_1':
      case 'PracticePitcherSingleSessionTiltChart_2':
        return t(chartConfig.axis);
      default:
        return '';
    }
  }, []);

  const getHideChartTitleInGrid = useCallback((chartId: ChartType) => {
    switch (chartId) {
      case 'BatterApproachPerCountChart':
        return true;
      default:
        return false;
    }
  }, []);

  return { getChartTitle, getChartSubTitle, getChartSidebarSubTitle, getHideChartTitleInGrid };
};

export const useGetUnitSystem = (): UnitSystem => {
  const profile = useGetProfileCache();

  if (profile?.preferredUnitSystem === 'Metric') {
    return 'Metric';
  }

  return 'Imperial';
};
